/**
 * Button Component
 * Reusable button component with various style variants
 *
 * @param {Object} props
 * @param {string} props.variant - Button color variant: 'blue', 'red', 'green', 'purple', 'gray'
 * @param {string} props.icon - Icon to display before text (emoji or symbol)
 * @param {React.ReactNode} props.children - Button text content
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {Function} props.onClick - Click handler
 * @param {string} props.type - Button type (button, submit, reset)
 * @param {Object} props.style - Additional inline styles
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.shadow - Whether to show shadow (default: true)
 * @param {boolean} props.fullWidth - Whether button should be full width
 * @param {string} props.size - Button size: 'small', 'medium', 'large'
 */
const Button = ({
  variant = 'blue',
  icon,
  children,
  disabled = false,
  onClick,
  type = 'button',
  style = {},
  className = '',
  shadow = true,
  fullWidth = false,
  size = 'medium',
  ...props
}) => {
  // Determine variant class
  const getVariantClass = () => {
    const variants = {
      blue: 'as-btn-blue',
      red: 'as-btn-red',
      green: 'as-btn-green',
      purple: 'as-btn-purple',
      gray: 'as-btn-gray',
    }
    return variants[variant] || 'as-btn-blue'
  }

  // Determine size padding
  const getSizePadding = () => {
    const sizes = {
      small: '5px 10px',
      medium: '7px 12px',
      large: '10px 16px',
    }
    return sizes[size] || sizes.medium
  }

  // Build class names
  const classes = [
    'as-btn-base',
    getVariantClass(),
    shadow && 'as-btn-with-shadow',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  // Build styles
  const buttonStyle = {
    padding: getSizePadding(),
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    whiteSpace: 'nowrap',
    width: fullWidth ? '100%' : 'auto',
    ...style,
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classes}
      style={buttonStyle}
      aria-disabled={disabled}
      {...props}
    >
      {icon && <span>{icon}</span>}
      {children}
    </button>
  )
}

export default Button
